import { useState } from 'react';
import { useWebsocketStore } from '../../../../stores/websocketStore/websocketProvider';
import style from './TradePlanHub.module.css';
import { Body2, Body3, Body4, fontSizes } from '../../../atomic/text/Text';
import { colors } from '../../../../colorVars';
import { filterTradePlansCallback } from './filterTradePlansCallback';
import { TradePlanHubControls } from './tradePlanHubControls/TradePlanHubControls';
import CloseWindowButton from '../../../atomic/buttons/closeWindow/CloseWindowButton';
import { observer } from 'mobx-react-lite';
import { BorderAndLabel } from '../../../atomic/atomicComponentLibrary/utility/borderAndLabel/BorderAndLabel';
import { TradePlanDisplay } from './tradePlanDisplay/TradePlanDisplay';

const sortMap = {
  bySymbol: 'tradePlanIdsSortedBySymbol',
  byStatus: 'tradePlanIdsSortedByStatus',
  byTemplate: 'tradePlanIdsSortedByTemplate',
  byCreationTime: 'tradePlanIdsSortedByCreateTime',
};
interface Props {
  moduleId: string;
  layoutType: string;
}

export const TradePlanHub = observer(({ moduleId, layoutType }: Props) => {
  const { tradePlanStore, layoutStore } = useWebsocketStore();
  const [showEntered, setShowEntered] = useState(true);
  const [showArmed, setShowArmed] = useState(true);
  const [showDeactivated, setShowDeactivated] = useState(true);
  const filterCallback = filterTradePlansCallback({
    showArmed,
    showDeactivated,
    showEntered,
  });
  const [sortingKeyword, setSortingKeyword] = useState('bySymbol');
  const getter = sortMap[sortingKeyword];
  const tradePlansIds: string[] = tradePlanStore[getter];
  const [selectedTradePlan, setSelectedTradePlan] = useState(null);
  const filteredTps = tradePlansIds.filter(filterCallback);

  const removeModule = () => {
    layoutStore.removeModuleFromCurrentLayout(layoutType, moduleId);
  };

  return (
    <div className={style.outerContainer}>
      <header className={style.header}>
        <div className={style.group}>
          <Body2 margin="0 5px 0 0" color={colors.lightGrey} fontWeight={700}>
            Trade Plan Hub
          </Body2>
        </div>

        <CloseWindowButton
          width={15}
          stroke={2}
          color={colors.red}
          handleClick={removeModule}
        />
      </header>
      <div className={style.innerContainer}>
        <div className={style.topControls}>
          <div>
            <Body3 color={colors.lightGrey}>Sort by: </Body3>
            <select onChange={({ target }) => setSortingKeyword(target.value)}>
              <option value="bySymbol">Symbol</option>
              <option value="byStatus">Status</option>
              <option value="byTemplate">Template</option>
              <option value="byCreationTime">Time</option>
            </select>
          </div>
          <BorderAndLabel
            label="Filter"
            color="white"
            fontSize={fontSizes.body4}
          >
            <div className={style.checkboxes}>
              <div className={style.labelAndCheckbox}>
                <input
                  type="checkbox"
                  checked={showEntered}
                  onClick={() => {
                    setShowEntered((prev) => !prev);
                    setSelectedTradePlan(null);
                  }}
                />
                <Body4 color={colors.lightGrey} fontWeight={500}>
                  Entered
                </Body4>
              </div>
              <div className={style.labelAndCheckbox}>
                <input
                  type="checkbox"
                  checked={showArmed}
                  onClick={() => {
                    setShowArmed((prev) => !prev);
                    setSelectedTradePlan(null);
                  }}
                />
                <Body4 color={colors.lightGrey} fontWeight={500}>
                  Armed
                </Body4>
              </div>
              <div className={style.labelAndCheckbox}>
                <input
                  type="checkbox"
                  checked={showDeactivated}
                  onClick={() => {
                    setShowDeactivated((prev) => !prev);
                    setSelectedTradePlan(null);
                  }}
                />
                <Body4 color={colors.lightGrey} fontWeight={500}>
                  Deactivated
                </Body4>
              </div>
            </div>
          </BorderAndLabel>
        </div>
        <TradePlanDisplay
          tradePlanIds={filteredTps}
          selectedTradePlan={selectedTradePlan}
          setSelectedTradePlan={setSelectedTradePlan}
        />
        <TradePlanHubControls selectedTradePlanId={selectedTradePlan} />
      </div>
    </div>
  );
});

/*
  - a list of all active trade plans
  - global controls for selected trade plan
  - sorting options, but do not allow them to move frequently
  - option for auto sort or sort on click
  - click or use arrow keys to select a trade plan
  - double click or click display to open a trade plan module in grid layout, or, if already open, highlight module
  - show running pnl totals etc
  - include current global controls(flatten all, cancel all)
  - allow using shift to select multiple plans

  Ethan's notes:
   TODO: add option details (ie strike date) underneath the symbol for position. ✅
   TODO: Add bid ask to top level. ✅
   TODO: Enter active icons different colors.
   TODO: Active not entered trade plan to have the entry in the hub. ✅
   TODO: If option config has no symbols, then flash red with warning.
   TODO: Maybe merge the exit columns?
   TODO: Filter by? ✅
   TODO: Space to display exits via icons + initials? 
   * two sections
    1. Trade Plan Managment
        - Cancel
        - Pause
        - Restart
        - Clear
    2. Trade Related
        -  

tradeplans: {
    "1653076064995": {
        "id": 1653076064995,
        "status": "ARMED",
        "brokerage": "BACKTEST",
        "activeOrderIds": [],
        "accountNumber": "BACKTEST",
        "goLong": true,
        "goShort": false,
        "instrument": "stock",
        "startPeriod": "open",
        "strategy": "BASIC_TRADING_SYSTEM",
        "setup": "BREAK",
        "quantityBasis": "DOLLARS",
        "workOrders": false,
        "workOrdersOnEntry": true,
        "workOrdersOnExit": true,
        "candleDurations": [],
        "terminateWhenFlat": true,
        "restartSystemOncePositionHasFlattened": false,
        "flattenAtMarketClose": false,
        "persistTradePlanOnFailedFiltersOrValidation": false,
        "inPersistingState": false,
        "symbol": "AAPL",
        "entryTactics": {
            "MARKET_ORDER": {
                "quantityBasisValue": 10000
            }
        },
        "exitTactics": {
            "LOSS_LIMIT": {
                "lossLimitMode": "AMOUNT",
                "amount": 100,
                "paused": false
            }
        },
        "deepCopyExitTactics": "{\"LOSS_LIMIT\":{\"lossLimitMode\":\"AMOUNT\",\"amount\":100.0,\"paused\":false}}",
        "positionTactics": {},
        "setupDetails": {
            "BREAK": {
                "tacticId": "BREAK|BASIC_TRADING_SYSTEM16530760654111",
                "priceToBreak": 5000
            }
        },
        "setupFilters": {},
        "totalTradePlanQty": 0,
        "getAwayOffsetPct": 0.1,
        "pctBetweenBidAsk": 0.2,
        "pctIncreasePerOrderAttempt": 0.01,
        "optionManagement": {
            "expirationDates": [],
            "buyOrSellOption": "LONG",
            "optionChainStrategy": {
                "useOptionManagement": false
            },
            "optionFilters": {},
            "ITM": false,
            "OTM": false
        },
        "startHour": 9,
        "startMin": 30,
        "inPosition": false,
        "savedConfigurationTitle": "marketOrder",
        "useSavedConfigTitleForDisplay": false,
        "createTimeStamp": 1653076065017,
        "paused": false,
        "restartSettings": {
            "maxRestartAttempts": 0,
            "maxLoss": 0,
            "maxMinutesInTradePlan": 0,
            "numberOfTrades": 0,
            "deactivateOnRestart": false,
            "restartsAttempted": 0,
            "minutesInTradePlan": 0,
            "realizedProfit": 0,
            "tradeCount": 0,
            "deactivated": false
        },
        "evaluateQuotes": true,
        "useDifferentWorkOrderFieldsOnExit": false,
        "getAwayOffsetPctOnExit": 0.1,
        "pctBetweenBidAskOnExit": 0.5,
        "pctIncreasePerOrderAttemptOnExit": 0.1,
        "useDifferentWorkOrderFieldsOnTarget": false,
        "getAwayOffsetPctOnTarget": 0.1,
        "pctBetweenBidAskOnTarget": 0.5,
        "pctIncreasePerOrderAttemptOnTarget": 0.1,
        "dataSource": "LIVE",
        "expanded": false
    },
    "1653076064997": {
        "id": 1653076064997,
        "status": "ARMED",
        "brokerage": "BACKTEST",
        "activeOrderIds": [],
        "accountNumber": "BACKTEST",
        "goLong": true,
        "goShort": false,
        "instrument": "stock",
        "startPeriod": "open",
        "strategy": "BASIC_TRADING_SYSTEM",
        "setup": "BREAK",
        "quantityBasis": "DOLLARS",
        "workOrders": false,
        "workOrdersOnEntry": true,
        "workOrdersOnExit": true,
        "candleDurations": [],
        "terminateWhenFlat": true,
        "restartSystemOncePositionHasFlattened": false,
        "flattenAtMarketClose": false,
        "persistTradePlanOnFailedFiltersOrValidation": false,
        "inPersistingState": false,
        "symbol": "TSLA",
        "entryTactics": {
            "MARKET_ORDER": {
                "quantityBasisValue": 10000
            }
        },
        "exitTactics": {
            "LOSS_LIMIT": {
                "lossLimitMode": "AMOUNT",
                "amount": 100,
                "paused": false
            }
        },
        "deepCopyExitTactics": "{\"LOSS_LIMIT\":{\"lossLimitMode\":\"AMOUNT\",\"amount\":100.0,\"paused\":false}}",
        "positionTactics": {},
        "setupDetails": {
            "BREAK": {
                "tacticId": "BREAK|BASIC_TRADING_SYSTEM16530760654131",
                "priceToBreak": 5000
            }
        },
        "setupFilters": {},
        "totalTradePlanQty": 0,
        "getAwayOffsetPct": 0.1,
        "pctBetweenBidAsk": 0.2,
        "pctIncreasePerOrderAttempt": 0.01,
        "optionManagement": {
            "expirationDates": [],
            "buyOrSellOption": "LONG",
            "optionChainStrategy": {
                "useOptionManagement": false
            },
            "optionFilters": {},
            "ITM": false,
            "OTM": false
        },
        "startHour": 9,
        "startMin": 30,
        "inPosition": false,
        "savedConfigurationTitle": "marketOrder",
        "useSavedConfigTitleForDisplay": false,
        "createTimeStamp": 1653076065017,
        "paused": false,
        "restartSettings": {
            "maxRestartAttempts": 0,
            "maxLoss": 0,
            "maxMinutesInTradePlan": 0,
            "numberOfTrades": 0,
            "deactivateOnRestart": false,
            "restartsAttempted": 0,
            "minutesInTradePlan": 0,
            "realizedProfit": 0,
            "tradeCount": 0,
            "deactivated": false
        },
        "evaluateQuotes": true,
        "useDifferentWorkOrderFieldsOnExit": false,
        "getAwayOffsetPctOnExit": 0.1,
        "pctBetweenBidAskOnExit": 0.5,
        "pctIncreasePerOrderAttemptOnExit": 0.1,
        "useDifferentWorkOrderFieldsOnTarget": false,
        "getAwayOffsetPctOnTarget": 0.1,
        "pctBetweenBidAskOnTarget": 0.5,
        "pctIncreasePerOrderAttemptOnTarget": 0.1,
        "dataSource": "LIVE",
        "expanded": false
    },
    "1653076064998": {
        "id": 1653076064998,
        "status": "ARMED",
        "brokerage": "BACKTEST",
        "activeOrderIds": [],
        "accountNumber": "BACKTEST",
        "goLong": true,
        "goShort": false,
        "instrument": "stock",
        "startPeriod": "open",
        "strategy": "BASIC_TRADING_SYSTEM",
        "setup": "BREAK",
        "quantityBasis": "DOLLARS",
        "workOrders": false,
        "workOrdersOnEntry": true,
        "workOrdersOnExit": true,
        "candleDurations": [],
        "terminateWhenFlat": true,
        "restartSystemOncePositionHasFlattened": false,
        "flattenAtMarketClose": false,
        "persistTradePlanOnFailedFiltersOrValidation": false,
        "inPersistingState": false,
        "symbol": "AMZN",
        "entryTactics": {
            "MARKET_ORDER": {
                "quantityBasisValue": 10000
            }
        },
        "exitTactics": {
            "LOSS_LIMIT": {
                "lossLimitMode": "AMOUNT",
                "amount": 100,
                "paused": false
            }
        },
        "deepCopyExitTactics": "{\"LOSS_LIMIT\":{\"lossLimitMode\":\"AMOUNT\",\"amount\":100.0,\"paused\":false}}",
        "positionTactics": {},
        "setupDetails": {
            "BREAK": {
                "tacticId": "BREAK|BASIC_TRADING_SYSTEM16530760654141",
                "priceToBreak": 5000
            }
        },
        "setupFilters": {},
        "totalTradePlanQty": 0,
        "getAwayOffsetPct": 0.1,
        "pctBetweenBidAsk": 0.2,
        "pctIncreasePerOrderAttempt": 0.01,
        "optionManagement": {
            "expirationDates": [],
            "buyOrSellOption": "LONG",
            "optionChainStrategy": {
                "useOptionManagement": false
            },
            "optionFilters": {},
            "ITM": false,
            "OTM": false
        },
        "startHour": 9,
        "startMin": 30,
        "inPosition": false,
        "savedConfigurationTitle": "marketOrder",
        "useSavedConfigTitleForDisplay": false,
        "createTimeStamp": 1653076065017,
        "paused": false,
        "restartSettings": {
            "maxRestartAttempts": 0,
            "maxLoss": 0,
            "maxMinutesInTradePlan": 0,
            "numberOfTrades": 0,
            "deactivateOnRestart": false,
            "restartsAttempted": 0,
            "minutesInTradePlan": 0,
            "realizedProfit": 0,
            "tradeCount": 0,
            "deactivated": false
        },
        "evaluateQuotes": true,
        "useDifferentWorkOrderFieldsOnExit": false,
        "getAwayOffsetPctOnExit": 0.1,
        "pctBetweenBidAskOnExit": 0.5,
        "pctIncreasePerOrderAttemptOnExit": 0.1,
        "useDifferentWorkOrderFieldsOnTarget": false,
        "getAwayOffsetPctOnTarget": 0.1,
        "pctBetweenBidAskOnTarget": 0.5,
        "pctIncreasePerOrderAttemptOnTarget": 0.1,
        "dataSource": "LIVE",
        "expanded": false
    },
    "1653076064996": {
        "id": 1653076064996,
        "status": "ARMED",
        "brokerage": "BACKTEST",
        "activeOrderIds": [],
        "accountNumber": "BACKTEST",
        "goLong": true,
        "goShort": false,
        "instrument": "stock",
        "startPeriod": "open",
        "strategy": "BASIC_TRADING_SYSTEM",
        "setup": "BREAK",
        "quantityBasis": "DOLLARS",
        "workOrders": false,
        "workOrdersOnEntry": true,
        "workOrdersOnExit": true,
        "candleDurations": [],
        "terminateWhenFlat": true,
        "restartSystemOncePositionHasFlattened": false,
        "flattenAtMarketClose": false,
        "persistTradePlanOnFailedFiltersOrValidation": false,
        "inPersistingState": false,
        "symbol": "AMD",
        "entryTactics": {
            "MARKET_ORDER": {
                "quantityBasisValue": 10000
            }
        },
        "exitTactics": {
            "LOSS_LIMIT": {
                "lossLimitMode": "AMOUNT",
                "amount": 100,
                "paused": false
            }
        },
        "deepCopyExitTactics": "{\"LOSS_LIMIT\":{\"lossLimitMode\":\"AMOUNT\",\"amount\":100.0,\"paused\":false}}",
        "positionTactics": {},
        "setupDetails": {
            "BREAK": {
                "tacticId": "BREAK|BASIC_TRADING_SYSTEM16530760654121",
                "priceToBreak": 5000
            }
        },
        "setupFilters": {},
        "totalTradePlanQty": 0,
        "getAwayOffsetPct": 0.1,
        "pctBetweenBidAsk": 0.2,
        "pctIncreasePerOrderAttempt": 0.01,
        "optionManagement": {
            "expirationDates": [],
            "buyOrSellOption": "LONG",
            "optionChainStrategy": {
                "useOptionManagement": false
            },
            "optionFilters": {},
            "ITM": false,
            "OTM": false
        },
        "startHour": 9,
        "startMin": 30,
        "inPosition": false,
        "savedConfigurationTitle": "marketOrder",
        "useSavedConfigTitleForDisplay": false,
        "createTimeStamp": 1653076065017,
        "paused": false,
        "restartSettings": {
            "maxRestartAttempts": 0,
            "maxLoss": 0,
            "maxMinutesInTradePlan": 0,
            "numberOfTrades": 0,
            "deactivateOnRestart": false,
            "restartsAttempted": 0,
            "minutesInTradePlan": 0,
            "realizedProfit": 0,
            "tradeCount": 0,
            "deactivated": false
        },
        "evaluateQuotes": true,
        "useDifferentWorkOrderFieldsOnExit": false,
        "getAwayOffsetPctOnExit": 0.1,
        "pctBetweenBidAskOnExit": 0.5,
        "pctIncreasePerOrderAttemptOnExit": 0.1,
        "useDifferentWorkOrderFieldsOnTarget": false,
        "getAwayOffsetPctOnTarget": 0.1,
        "pctBetweenBidAskOnTarget": 0.5,
        "pctIncreasePerOrderAttemptOnTarget": 0.1,
        "dataSource": "LIVE",
        "expanded": false
    },
    1653076572527: {
    "id": 1653076572527,
    "status": "ARMED",
    "brokerage": "BACKTEST",
    "activeOrderIds": [],
    "accountNumber": "BACKTEST",
    "goLong": true,
    "goShort": false,
    "instrument": "option",
    "startPeriod": "open",
    "strategy": "BASIC_TRADING_SYSTEM",
    "setup": "BREAK",
    "quantityBasis": "DOLLARS",
    "workOrders": false,
    "workOrdersOnEntry": true,
    "workOrdersOnExit": true,
    "candleDurations": [],
    "terminateWhenFlat": true,
    "restartSystemOncePositionHasFlattened": false,
    "flattenAtMarketClose": false,
    "persistTradePlanOnFailedFiltersOrValidation": false,
    "inPersistingState": false,
    "symbol": "AAPL",
    "entryTactics": {
        "MARKET_ORDER": {
            "quantityBasisValue": 1000
        }
    },
    "exitTactics": {
        "LOSS_LIMIT": {
            "lossLimitMode": "AMOUNT",
            "amount": 100,
            "paused": false
        }
    },
    "deepCopyExitTactics": "{\"LOSS_LIMIT\":{\"lossLimitMode\":\"AMOUNT\",\"amount\":100.0,\"paused\":false}}",
    "positionTactics": {},
    "setupDetails": {
        "BREAK": {
            "tacticId": "BREAK|BASIC_TRADING_SYSTEM16530760654151",
            "priceToBreak": 1000
        }
    },
    "setupFilters": {},
    "totalTradePlanQty": 0,
    "getAwayOffsetPct": 0.1,
    "pctBetweenBidAsk": 0.2,
    "pctIncreasePerOrderAttempt": 0.01,
    "optionManagement": {
        "expirationDates": [
            "2022-05-20",
            "2022-05-27"
        ],
        "buyOrSellOption": "LONG",
        "optionChainStrategy": {
            "hugCurrentPrice": {
                "numOfStrikes": 5
            },
            "useOptionManagement": true
        },
        "optionFilters": {
            "optionPriceRange": {
                "minPrice": 0,
                "maxPrice": 10
            },
            "tightSpreadPct": {
                "maxOptionSpreadPct": 0.05
            }
        },
        "optionChoices": {
            "dataSource": "LIVE",
            "choices": {
                "2022-05-20": [
                    "AAPL220520C00055000",
                    "AAPL220520C00060000",
                    "AAPL220520C00065000",
                    "AAPL220520C00070000",
                    "AAPL220520C00075000",
                    "AAPL220520C00080000",
                    "AAPL220520C00085000",
                    "AAPL220520C00090000",
                    "AAPL220520C00095000",
                    "AAPL220520C00100000",
                    "AAPL220520C00105000",
                    "AAPL220520C00110000",
                    "AAPL220520C00115000",
                    "AAPL220520C00120000",
                    "AAPL220520C00125000",
                    "AAPL220520C00126000",
                    "AAPL220520C00127000",
                    "AAPL220520C00128000",
                    "AAPL220520C00129000",
                    "AAPL220520C00130000",
                    "AAPL220520C00131000",
                    "AAPL220520C00132000",
                    "AAPL220520C00133000",
                    "AAPL220520C00134000",
                    "AAPL220520C00135000",
                    "AAPL220520C00136000",
                    "AAPL220520C00137000",
                    "AAPL220520C00138000",
                    "AAPL220520C00139000",
                    "AAPL220520C00140000",
                    "AAPL220520C00141000",
                    "AAPL220520C00142000",
                    "AAPL220520C00143000",
                    "AAPL220520C00144000",
                    "AAPL220520C00145000",
                    "AAPL220520C00146000",
                    "AAPL220520C00147000",
                    "AAPL220520C00148000",
                    "AAPL220520C00149000",
                    "AAPL220520C00150000",
                    "AAPL220520C00152500",
                    "AAPL220520C00155000",
                    "AAPL220520C00157500",
                    "AAPL220520C00160000",
                    "AAPL220520C00162500",
                    "AAPL220520C00165000",
                    "AAPL220520C00167500",
                    "AAPL220520C00170000",
                    "AAPL220520C00172500",
                    "AAPL220520C00175000",
                    "AAPL220520C00177500",
                    "AAPL220520C00180000",
                    "AAPL220520C00182500",
                    "AAPL220520C00185000",
                    "AAPL220520C00187500",
                    "AAPL220520C00190000",
                    "AAPL220520C00192500",
                    "AAPL220520C00195000",
                    "AAPL220520C00200000",
                    "AAPL220520C00205000",
                    "AAPL220520C00210000",
                    "AAPL220520C00215000",
                    "AAPL220520C00220000",
                    "AAPL220520C00225000",
                    "AAPL220520C00230000",
                    "AAPL220520C00235000",
                    "AAPL220520C00240000",
                    "AAPL220520C00245000",
                    "AAPL220520C00250000",
                    "AAPL220520C00255000",
                    "AAPL220520C00260000",
                    "AAPL220520C00270000",
                    "AAPL220520C00280000",
                    "AAPL220520C00290000",
                    "AAPL220520C00300000"
                ],
                "2022-05-27": [
                    "AAPL220527C00095000",
                    "AAPL220527C00100000",
                    "AAPL220527C00105000",
                    "AAPL220527C00110000",
                    "AAPL220527C00115000",
                    "AAPL220527C00116000",
                    "AAPL220527C00117000",
                    "AAPL220527C00118000",
                    "AAPL220527C00119000",
                    "AAPL220527C00120000",
                    "AAPL220527C00121000",
                    "AAPL220527C00122000",
                    "AAPL220527C00123000",
                    "AAPL220527C00124000",
                    "AAPL220527C00125000",
                    "AAPL220527C00126000",
                    "AAPL220527C00127000",
                    "AAPL220527C00128000",
                    "AAPL220527C00129000",
                    "AAPL220527C00130000",
                    "AAPL220527C00131000",
                    "AAPL220527C00132000",
                    "AAPL220527C00133000",
                    "AAPL220527C00134000",
                    "AAPL220527C00135000",
                    "AAPL220527C00136000",
                    "AAPL220527C00137000",
                    "AAPL220527C00138000",
                    "AAPL220527C00139000",
                    "AAPL220527C00140000",
                    "AAPL220527C00141000",
                    "AAPL220527C00142000",
                    "AAPL220527C00143000",
                    "AAPL220527C00144000",
                    "AAPL220527C00145000",
                    "AAPL220527C00146000",
                    "AAPL220527C00147000",
                    "AAPL220527C00148000",
                    "AAPL220527C00149000",
                    "AAPL220527C00150000",
                    "AAPL220527C00152500",
                    "AAPL220527C00155000",
                    "AAPL220527C00157500",
                    "AAPL220527C00160000",
                    "AAPL220527C00162500",
                    "AAPL220527C00165000",
                    "AAPL220527C00167500",
                    "AAPL220527C00170000",
                    "AAPL220527C00172500",
                    "AAPL220527C00175000",
                    "AAPL220527C00180000",
                    "AAPL220527C00185000",
                    "AAPL220527C00190000",
                    "AAPL220527C00195000",
                    "AAPL220527C00200000",
                    "AAPL220527C00205000",
                    "AAPL220527C00210000",
                    "AAPL220527C00215000",
                    "AAPL220527C00220000",
                    "AAPL220527C00225000",
                    "AAPL220527C00230000",
                    "AAPL220527C00235000",
                    "AAPL220527C00240000",
                    "AAPL220527C00245000",
                    "AAPL220527C00250000"
                ]
            }
        },
        "ITM": false,
        "OTM": false
    },
    "startHour": 9,
    "startMin": 30,
    "inPosition": false,
    "savedConfigurationTitle": "options",
    "useSavedConfigTitleForDisplay": false,
    "createTimeStamp": 1653076572529,
    "paused": false,
    "restartSettings": {
        "maxRestartAttempts": 0,
        "maxLoss": 0,
        "maxMinutesInTradePlan": 0,
        "numberOfTrades": 0,
        "deactivateOnRestart": false,
        "restartsAttempted": 0,
        "minutesInTradePlan": 0,
        "realizedProfit": 0,
        "tradeCount": 0,
        "deactivated": false
    },
    "evaluateQuotes": true,
    "useDifferentWorkOrderFieldsOnExit": false,
    "getAwayOffsetPctOnExit": 0.1,
    "pctBetweenBidAskOnExit": 0.5,
    "pctIncreasePerOrderAttemptOnExit": 0.1,
    "useDifferentWorkOrderFieldsOnTarget": false,
    "getAwayOffsetPctOnTarget": 0.1,
    "pctBetweenBidAskOnTarget": 0.5,
    "pctIncreasePerOrderAttemptOnTarget": 0.1,
    "dataSource": "LIVE",
    "expanded": false
}
}


{
    "id": 1653076572527,
    "status": "ARMED",
    "brokerage": "BACKTEST",
    "activeOrderIds": [],
    "accountNumber": "BACKTEST",
    "goLong": true,
    "goShort": false,
    "instrument": "option",
    "startPeriod": "open",
    "strategy": "BASIC_TRADING_SYSTEM",
    "setup": "BREAK",
    "quantityBasis": "DOLLARS",
    "workOrders": false,
    "workOrdersOnEntry": true,
    "workOrdersOnExit": true,
    "candleDurations": [],
    "terminateWhenFlat": true,
    "restartSystemOncePositionHasFlattened": false,
    "flattenAtMarketClose": false,
    "persistTradePlanOnFailedFiltersOrValidation": false,
    "inPersistingState": false,
    "symbol": "AAPL",
    "entryTactics": {
        "MARKET_ORDER": {
            "quantityBasisValue": 1000
        }
    },
    "exitTactics": {
        "LOSS_LIMIT": {
            "lossLimitMode": "AMOUNT",
            "amount": 100,
            "paused": false
        }
    },
    "deepCopyExitTactics": "{\"LOSS_LIMIT\":{\"lossLimitMode\":\"AMOUNT\",\"amount\":100.0,\"paused\":false}}",
    "positionTactics": {},
    "setupDetails": {
        "BREAK": {
            "tacticId": "BREAK|BASIC_TRADING_SYSTEM16530760654151",
            "priceToBreak": 1000
        }
    },
    "setupFilters": {},
    "totalTradePlanQty": 0,
    "getAwayOffsetPct": 0.1,
    "pctBetweenBidAsk": 0.2,
    "pctIncreasePerOrderAttempt": 0.01,
    "optionManagement": {
        "expirationDates": [
            "2022-05-20",
            "2022-05-27"
        ],
        "buyOrSellOption": "LONG",
        "optionChainStrategy": {
            "hugCurrentPrice": {
                "numOfStrikes": 5
            },
            "useOptionManagement": true
        },
        "optionFilters": {
            "optionPriceRange": {
                "minPrice": 0,
                "maxPrice": 10
            },
            "tightSpreadPct": {
                "maxOptionSpreadPct": 0.05
            }
        },
        "optionChoices": {
            "dataSource": "LIVE",
            "choices": {
                "2022-05-20": [
                    "AAPL220520C00055000",
                    "AAPL220520C00060000",
                    "AAPL220520C00065000",
                    "AAPL220520C00070000",
                    "AAPL220520C00075000",
                    "AAPL220520C00080000",
                    "AAPL220520C00085000",
                    "AAPL220520C00090000",
                    "AAPL220520C00095000",
                    "AAPL220520C00100000",
                    "AAPL220520C00105000",
                    "AAPL220520C00110000",
                    "AAPL220520C00115000",
                    "AAPL220520C00120000",
                    "AAPL220520C00125000",
                    "AAPL220520C00126000",
                    "AAPL220520C00127000",
                    "AAPL220520C00128000",
                    "AAPL220520C00129000",
                    "AAPL220520C00130000",
                    "AAPL220520C00131000",
                    "AAPL220520C00132000",
                    "AAPL220520C00133000",
                    "AAPL220520C00134000",
                    "AAPL220520C00135000",
                    "AAPL220520C00136000",
                    "AAPL220520C00137000",
                    "AAPL220520C00138000",
                    "AAPL220520C00139000",
                    "AAPL220520C00140000",
                    "AAPL220520C00141000",
                    "AAPL220520C00142000",
                    "AAPL220520C00143000",
                    "AAPL220520C00144000",
                    "AAPL220520C00145000",
                    "AAPL220520C00146000",
                    "AAPL220520C00147000",
                    "AAPL220520C00148000",
                    "AAPL220520C00149000",
                    "AAPL220520C00150000",
                    "AAPL220520C00152500",
                    "AAPL220520C00155000",
                    "AAPL220520C00157500",
                    "AAPL220520C00160000",
                    "AAPL220520C00162500",
                    "AAPL220520C00165000",
                    "AAPL220520C00167500",
                    "AAPL220520C00170000",
                    "AAPL220520C00172500",
                    "AAPL220520C00175000",
                    "AAPL220520C00177500",
                    "AAPL220520C00180000",
                    "AAPL220520C00182500",
                    "AAPL220520C00185000",
                    "AAPL220520C00187500",
                    "AAPL220520C00190000",
                    "AAPL220520C00192500",
                    "AAPL220520C00195000",
                    "AAPL220520C00200000",
                    "AAPL220520C00205000",
                    "AAPL220520C00210000",
                    "AAPL220520C00215000",
                    "AAPL220520C00220000",
                    "AAPL220520C00225000",
                    "AAPL220520C00230000",
                    "AAPL220520C00235000",
                    "AAPL220520C00240000",
                    "AAPL220520C00245000",
                    "AAPL220520C00250000",
                    "AAPL220520C00255000",
                    "AAPL220520C00260000",
                    "AAPL220520C00270000",
                    "AAPL220520C00280000",
                    "AAPL220520C00290000",
                    "AAPL220520C00300000"
                ],
                "2022-05-27": [
                    "AAPL220527C00095000",
                    "AAPL220527C00100000",
                    "AAPL220527C00105000",
                    "AAPL220527C00110000",
                    "AAPL220527C00115000",
                    "AAPL220527C00116000",
                    "AAPL220527C00117000",
                    "AAPL220527C00118000",
                    "AAPL220527C00119000",
                    "AAPL220527C00120000",
                    "AAPL220527C00121000",
                    "AAPL220527C00122000",
                    "AAPL220527C00123000",
                    "AAPL220527C00124000",
                    "AAPL220527C00125000",
                    "AAPL220527C00126000",
                    "AAPL220527C00127000",
                    "AAPL220527C00128000",
                    "AAPL220527C00129000",
                    "AAPL220527C00130000",
                    "AAPL220527C00131000",
                    "AAPL220527C00132000",
                    "AAPL220527C00133000",
                    "AAPL220527C00134000",
                    "AAPL220527C00135000",
                    "AAPL220527C00136000",
                    "AAPL220527C00137000",
                    "AAPL220527C00138000",
                    "AAPL220527C00139000",
                    "AAPL220527C00140000",
                    "AAPL220527C00141000",
                    "AAPL220527C00142000",
                    "AAPL220527C00143000",
                    "AAPL220527C00144000",
                    "AAPL220527C00145000",
                    "AAPL220527C00146000",
                    "AAPL220527C00147000",
                    "AAPL220527C00148000",
                    "AAPL220527C00149000",
                    "AAPL220527C00150000",
                    "AAPL220527C00152500",
                    "AAPL220527C00155000",
                    "AAPL220527C00157500",
                    "AAPL220527C00160000",
                    "AAPL220527C00162500",
                    "AAPL220527C00165000",
                    "AAPL220527C00167500",
                    "AAPL220527C00170000",
                    "AAPL220527C00172500",
                    "AAPL220527C00175000",
                    "AAPL220527C00180000",
                    "AAPL220527C00185000",
                    "AAPL220527C00190000",
                    "AAPL220527C00195000",
                    "AAPL220527C00200000",
                    "AAPL220527C00205000",
                    "AAPL220527C00210000",
                    "AAPL220527C00215000",
                    "AAPL220527C00220000",
                    "AAPL220527C00225000",
                    "AAPL220527C00230000",
                    "AAPL220527C00235000",
                    "AAPL220527C00240000",
                    "AAPL220527C00245000",
                    "AAPL220527C00250000"
                ]
            }
        },
        "ITM": false,
        "OTM": false
    },
    "startHour": 9,
    "startMin": 30,
    "inPosition": false,
    "savedConfigurationTitle": "options",
    "useSavedConfigTitleForDisplay": false,
    "createTimeStamp": 1653076572529,
    "paused": false,
    "restartSettings": {
        "maxRestartAttempts": 0,
        "maxLoss": 0,
        "maxMinutesInTradePlan": 0,
        "numberOfTrades": 0,
        "deactivateOnRestart": false,
        "restartsAttempted": 0,
        "minutesInTradePlan": 0,
        "realizedProfit": 0,
        "tradeCount": 0,
        "deactivated": false
    },
    "evaluateQuotes": true,
    "useDifferentWorkOrderFieldsOnExit": false,
    "getAwayOffsetPctOnExit": 0.1,
    "pctBetweenBidAskOnExit": 0.5,
    "pctIncreasePerOrderAttemptOnExit": 0.1,
    "useDifferentWorkOrderFieldsOnTarget": false,
    "getAwayOffsetPctOnTarget": 0.1,
    "pctBetweenBidAskOnTarget": 0.5,
    "pctIncreasePerOrderAttemptOnTarget": 0.1,
    "dataSource": "LIVE",
    "expanded": false
}
*/
